import { ClipboardList, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { Select, type SelectOption } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { apiErrorMessage } from '@/i18n'
import { BOARD_PRIORITIES, type Board, type BoardTask, type BoardTaskInput, useSaveBoardTask } from './api'

type Props = {
  board: Board
  task: BoardTask | null
  defaultColumn?: string
  people: SelectOption[]
  customers: SelectOption[]
  projects: SelectOption[]
  onClose: () => void
}

/** Pano görevi oluşturma/düzenleme. Müşteri yalnız oluştururken seçilir (görev müşteri kaydına bağlanır). */
export function BoardTaskModal({ board, task, defaultColumn, people, customers, projects, onClose }: Props) {
  const { t } = useTranslation()
  const save = useSaveBoardTask(board.id)
  const [form, setForm] = useState<BoardTaskInput>(() => ({
    title: task?.title ?? '',
    description: task?.description ?? '',
    columnKey: task?.columnKey ?? defaultColumn ?? board.columns[0]?.key ?? '',
    assigneeId: task?.assigneeId ?? '',
    priority: task?.priority ?? 'normal',
    dueDate: task?.dueAt ? task.dueAt.slice(0, 10) : '',
    accountId: task?.accountId ?? '',
    projectId: task?.projectId ?? '',
  }))
  const [submitted, setSubmitted] = useState(false)
  const set = <K extends keyof BoardTaskInput>(k: K, v: BoardTaskInput[K]) => setForm((f) => ({ ...f, [k]: v }))
  const titleError = submitted && !form.title.trim() ? t('projects.board.titleRequired') : undefined
  const none = { value: '', label: t('projects.board.none') }

  const submit = () => {
    setSubmitted(true)
    if (!form.title.trim()) return
    save.mutate(
      { id: task?.id, input: form },
      {
        onSuccess: () => {
          toast.success(t(task ? 'projects.board.updated' : 'projects.board.created'))
          onClose()
        },
        onError: (e) => toast.error(apiErrorMessage(e)),
      },
    )
  }

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onClose()}
      dismissible={!save.isPending}
      icon={<ClipboardList />}
      title={task ? t('projects.board.editTitle') : t('projects.board.createTitle', { board: board.name })}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="bt-title">{t('projects.board.fields.title')}</Label>
          <Input id="bt-title" maxLength={255} value={form.title} onChange={(e) => set('title', e.target.value)} aria-invalid={!!titleError} />
          {titleError ? <p className="text-xs text-destructive">{titleError}</p> : null}
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="bt-desc">{t('projects.board.fields.description')}</Label>
          <Textarea id="bt-desc" rows={4} maxLength={4000} value={form.description} onChange={(e) => set('description', e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bt-column">{t('projects.board.fields.column')}</Label>
          <Select id="bt-column" value={form.columnKey} onChange={(v) => set('columnKey', v)} options={board.columns.map((c) => ({ value: c.key, label: c.name }))} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bt-priority">{t('projects.board.fields.priority')}</Label>
          <Select id="bt-priority" value={form.priority} onChange={(v) => set('priority', v)} options={BOARD_PRIORITIES.map((p) => ({ value: p, label: t(`projects.rules.priority.${p}`) }))} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bt-assignee">{t('projects.board.fields.assignee')}</Label>
          <Select id="bt-assignee" searchable value={form.assigneeId} onChange={(v) => set('assigneeId', v)} options={[none, ...people]} placeholder={t('projects.work.unassigned')} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bt-due">{t('projects.board.fields.due')}</Label>
          <DateField id="bt-due" value={form.dueDate} onChange={(v) => set('dueDate', v)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bt-customer">{t('projects.board.fields.customer')}</Label>
          <Select id="bt-customer" searchable disabled={!!task} value={form.accountId} onChange={(v) => set('accountId', v)} options={[none, ...customers]} placeholder={t('projects.board.none')} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bt-project">{t('projects.board.fields.project')}</Label>
          <Select id="bt-project" searchable value={form.projectId} onChange={(v) => set('projectId', v)} options={[none, ...projects]} placeholder={t('projects.board.none')} />
        </div>
      </div>
    </Modal>
  )
}
